import { keyframes, css } from 'styled-components';
import { duration, easing } from './theme';

// ─────────────────────────────────────────────────────────────────────────────
// KEYFRAME DEFINITIONS
// All animations are defined once and exported for reuse across the app.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fade animations.
 */
export const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

export const fadeOut = keyframes`
  from { opacity: 1; }
  to   { opacity: 0; }
`;

/**
 * Slide animations.
 */
export const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-24px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

export const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(24px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

/**
 * Scale animations (modals, popovers).
 */
export const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

export const scaleOut = keyframes`
  from {
    opacity: 1;
    transform: scale(1);
  }
  to {
    opacity: 0;
    transform: scale(0.9);
  }
`;

/**
 * Pulse animation (notifications, streaks, live indicators).
 */
export const pulse = keyframes`
  0%   { transform: scale(1); opacity: 1; }
  50%  { transform: scale(1.05); opacity: 0.8; }
  100% { transform: scale(1); opacity: 1; }
`;

/**
 * Shake animation (error states, invalid input).
 */
export const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
  20%, 40%, 60%, 80% { transform: translateX(4px); }
`;

/**
 * Heartbeat animation (like button - spring feel).
 */
export const heartbeat = keyframes`
  0%   { transform: scale(1); }
  15%  { transform: scale(1.3); }
  30%  { transform: scale(0.95); }
  45%  { transform: scale(1.1); }
  60%  { transform: scale(1); }
  100% { transform: scale(1); }
`;

/**
 * Shimmer animation for skeleton loading.
 * Sweeps a light gradient from left to right.
 */
export const shimmer = keyframes`
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
`;

/**
 * Confetti burst (CSS-only). Generates expanding, rotating particles.
 * Use on pseudo-elements for a celebratory effect.
 */
export const confettiBurst = keyframes`
  0% {
    opacity: 1;
    transform: scale(0) rotate(0deg);
  }
  50% {
    opacity: 1;
    transform: scale(1.2) rotate(180deg);
  }
  100% {
    opacity: 0;
    transform: scale(1.5) rotate(360deg);
  }
`;

/**
 * Spin animation (loaders).
 */
export const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

/**
 * Bounce in animation.
 */
export const bounceIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.3);
  }
  50% {
    transform: scale(1.05);
  }
  70% {
    transform: scale(0.9);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

/**
 * Glow pulse (for cards with colored glow on hover).
 */
export const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(17, 204, 17, 0.1); }
  50%      { box-shadow: 0 0 30px rgba(17, 204, 17, 0.2); }
`;

/**
 * Flame pulse animation for streak indicators.
 * Subtle flicker effect to draw attention.
 */
export const flamePulse = keyframes`
  0%, 100% {
    transform: scale(1);
    filter: brightness(1);
  }
  25% {
    transform: scale(1.05) rotate(-2deg);
    filter: brightness(1.1);
  }
  50% {
    transform: scale(1.08);
    filter: brightness(1.15);
  }
  75% {
    transform: scale(1.03) rotate(2deg);
    filter: brightness(1.05);
  }
`;

/**
 * Success checkmark animation.
 * Brief scale-in + check drawing for form submit success.
 */
export const successCheck = keyframes`
  0% {
    transform: scale(0.5);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

/**
 * Slide out right animation (for swipe-to-dismiss).
 */
export const slideOutRight = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// ANIMATION HELPERS (CSS mixins)
// Apply these via styled-components template literals.
// All respect prefers-reduced-motion automatically.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Wraps an animation declaration so that it respects prefers-reduced-motion.
 * Usage: ${motionSafe(css`animation: ${fadeIn} 300ms ease-out;`)}
 */
export const motionSafe = (animationCss) => css`
  @media (prefers-reduced-motion: no-preference) {
    ${animationCss}
  }
`;

/**
 * Pre-built animation mixins.
 */
export const animations = {
  fadeIn: css`
    @media (prefers-reduced-motion: no-preference) {
      animation: ${fadeIn} ${duration.normal} ${easing.easeOut} both;
    }
  `,
  fadeOut: css`
    @media (prefers-reduced-motion: no-preference) {
      animation: ${fadeOut} ${duration.fast} ${easing.easeOut} both;
    }
  `,
  slideUp: css`
    @media (prefers-reduced-motion: no-preference) {
      animation: ${slideUp} ${duration.normal} ${easing.easeOut} both;
    }
  `,
  slideDown: css`
    @media (prefers-reduced-motion: no-preference) {
      animation: ${slideDown} ${duration.normal} ${easing.easeOut} both;
    }
  `,
  slideInLeft: css`
    @media (prefers-reduced-motion: no-preference) {
      animation: ${slideInLeft} ${duration.normal} ${easing.easeOut} both;
    }
  `,
  slideInRight: css`
    @media (prefers-reduced-motion: no-preference) {
      animation: ${slideInRight} ${duration.normal} ${easing.easeOut} both;
    }
  `,
  scaleIn: css`
    @media (prefers-reduced-motion: no-preference) {
      animation: ${scaleIn} ${duration.normal} ${easing.spring} both;
    }
  `,
  scaleOut: css`
    @media (prefers-reduced-motion: no-preference) {
      animation: ${scaleOut} ${duration.fast} ${easing.easeOut} both;
    }
  `,
  pulse: css`
    @media (prefers-reduced-motion: no-preference) {
      animation: ${pulse} ${duration.glacial} ${easing.easeInOut} infinite;
    }
  `,
  shake: css`
    @media (prefers-reduced-motion: no-preference) {
      animation: ${shake} ${duration.slow} ${easing.easeOut} both;
    }
  `,
  heartbeat: css`
    @media (prefers-reduced-motion: no-preference) {
      animation: ${heartbeat} ${duration.slow} ${easing.spring} both;
    }
  `,
  spin: css`
    @media (prefers-reduced-motion: no-preference) {
      animation: ${spin} ${duration.glacial} linear infinite;
    }
  `,
  bounceIn: css`
    @media (prefers-reduced-motion: no-preference) {
      animation: ${bounceIn} ${duration.slow} ${easing.spring} both;
    }
  `,
  flamePulse: css`
    @media (prefers-reduced-motion: no-preference) {
      animation: ${flamePulse} 2s ${easing.easeInOut} infinite;
    }
  `,
  successCheck: css`
    @media (prefers-reduced-motion: no-preference) {
      animation: ${successCheck} ${duration.normal} ${easing.spring} both;
    }
  `,
};

/**
 * Shimmer mixin for skeleton loaders.
 * Apply directly to a styled component.
 * Provides the moving gradient background.
 */
export const shimmerStyle = css`
  background: ${({ theme }) =>
    `linear-gradient(
      90deg,
      ${theme.colors.skeletonBase} 0%,
      ${theme.colors.skeletonHighlight} 50%,
      ${theme.colors.skeletonBase} 100%
    )`};
  background-size: 936px 100%;

  @media (prefers-reduced-motion: no-preference) {
    animation: ${shimmer} 1.5s ease-in-out infinite;
  }
`;
